/**
 * Inbox + capture hooks.
 *
 * GET /inbox returns the active inbox items; POST /inbox/capture creates one.
 * Both invalidate the same query keys so the inbox list refreshes after a
 * capture or triage without a manual refetch.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend } from './client';
import type { TaskDto } from './tasks';

export function useInbox() {
  return useQuery({
    queryKey: ['inbox'],
    queryFn: () => apiGet<{ tasks: TaskDto[] }>('/inbox'),
    staleTime: 10_000,
  });
}

export interface CaptureInput {
  title: string;
  description?: string | null;
  sourceKind?: string | null;
  sourceRef?: string | null;
  // Structured attachments from the quick-capture slash menu. When `status` is
  // not `inbox`, the task lands directly in that status (skipping triage).
  status?:
    | 'inbox'
    | 'next'
    | 'scheduled'
    | 'waiting'
    | 'delegated'
    | 'blocked'
    | 'someday'
    | 'done';
  priority?: 'critical' | 'important' | 'routine' | 'low';
  contextId?: string | null;
  projectId?: string | null;
  areaId?: string | null;
  personId?: string | null;
  dueAt?: string | null;
  scheduledAt?: string | null;
}

export function useCapture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CaptureInput) =>
      apiSend<{ task: TaskDto }>('POST', '/inbox/capture', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['today'] });
      qc.invalidateQueries({ queryKey: ['upcoming'] });
      qc.invalidateQueries({ queryKey: ['waiting'] });
    },
  });
}

export interface TriageInput {
  status: 'next' | 'scheduled' | 'waiting' | 'delegated' | 'blocked' | 'someday' | 'done';
  contextId?: string | null;
  projectId?: string | null;
  areaId?: string | null;
  personId?: string | null;
  dueAt?: string | null;
  scheduledAt?: string | null;
  reviewAt?: string | null;
  waitingFor?: string | null;
  /** Note recorded against the task as a `commented` event. Required by the
   *  Inbox's "Accept and close" action so a task filed straight to `done`
   *  carries a record of what was actually done. */
  comment?: string | null;
}

export function useTriage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: TriageInput }) =>
      apiSend<{ task: TaskDto }>('POST', `/inbox/${vars.id}/triage`, vars.input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      // Triage lands the task in a status these views read, so refresh them
      // too — otherwise an accepted task lingers stale until staleTime lapses.
      qc.invalidateQueries({ queryKey: ['today'] });
      qc.invalidateQueries({ queryKey: ['upcoming'] });
      qc.invalidateQueries({ queryKey: ['waiting'] });
      qc.invalidateQueries({ queryKey: ['task', vars.id] });
    },
  });
}

export function useDiscardInbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiSend<{ task: TaskDto }>('POST', `/inbox/${id}/discard`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
